/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';

import { KAI_SHIKI_LAYOUT, KAI_SHIKI_TYPOGRAPHY } from '@/Client/Styles/kaiShikiTokens';
import { PageLayout } from '../Layout';
import { usePageTitle } from '../Lib';
import { useAuth, useKaiProApiClient, useToast } from '../State';

const PAGE_STYLE = css`
  label: ISSUE_VIDEO_UPLOADS_ADMIN_PAGE_STYLE;
  max-width: 56rem;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing2}px;
`;

const CARD_STYLE = css`
  label: ISSUE_VIDEO_UPLOADS_ADMIN_CARD_STYLE;
  background: var(--background);
  border: 1px solid var(--border);
  padding: ${KAI_SHIKI_LAYOUT.spacing3}px;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing2}px;
`;

const INTRO_STYLE = css`
  label: ISSUE_VIDEO_UPLOADS_ADMIN_INTRO_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.medium}
  color: var(--text2);
`;

const LABEL_STYLE = css`
  label: ISSUE_VIDEO_UPLOADS_ADMIN_LABEL_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.smallBold}
  color: var(--text);
  text-transform: uppercase;
`;

const INPUT_STYLE = css`
  label: ISSUE_VIDEO_UPLOADS_ADMIN_INPUT_STYLE;
  width: 100%;
  border: 1px solid var(--border);
  background: var(--background2);
  color: var(--text);
  padding: ${KAI_SHIKI_LAYOUT.spacing1}px;
  ${KAI_SHIKI_TYPOGRAPHY.medium}
`;

const LIST_STYLE = css`
  label: ISSUE_VIDEO_UPLOADS_ADMIN_LIST_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing2}px;
`;

const ITEM_STYLE = css`
  label: ISSUE_VIDEO_UPLOADS_ADMIN_ITEM_STYLE;
  background: var(--background);
  border: 1px solid var(--border);
  padding: ${KAI_SHIKI_LAYOUT.spacing2}px;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing1}px;
`;

export const IssueVideoUploadsAdminPage = () => {
  usePageTitle('All User Video Uploads');
  const kaiProApiClient = useKaiProApiClient();
  const { isSuperAdmin } = useAuth();
  const { showErrorToast } = useToast();

  const [projectId, setProjectId] = useState('');
  const [scanId, setScanId] = useState('');
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadSubmissions = useCallback(async (nextProjectId?: string, nextScanId?: string) => {
    try {
      setLoading(true);
      const response = await kaiProApiClient.listIssueVideoSubmissions({
        projectId: nextProjectId?.trim() || undefined,
        scanId: nextScanId?.trim() || undefined,
        limit: 25,
      });
      setSubmissions(response.submissions);
    } catch (error: any) {
      setSubmissions([]);
      showErrorToast(error?.message || 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }, [kaiProApiClient, showErrorToast]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const timeout = window.setTimeout(() => {
      void loadSubmissions(projectId || undefined, scanId || undefined);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [isSuperAdmin, loadSubmissions, projectId, scanId]);

  if (!isSuperAdmin) {
    return (
      <PageLayout title='All User Video Uploads' contentLayoutStyling='standard'>
        <div css={PAGE_STYLE}>
          <div css={CARD_STYLE}>
            <div css={INTRO_STYLE}>Super admin access is required.</div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title='All User Video Uploads' contentLayoutStyling='standard'>
      <div css={PAGE_STYLE}>
        <div css={CARD_STYLE}>
          <div css={INTRO_STYLE}>
            Super admin view. This page shows completed video uploads from all users, not just your own. Each card shows who uploaded the video. Narrow by
            project or scan if needed.
          </div>

          <label css={LABEL_STYLE} htmlFor='adminProjectId'>Project ID (optional)</label>
          <input
            id='adminProjectId'
            css={INPUT_STYLE}
            value={projectId}
            onChange={event => setProjectId(event.target.value)}
            placeholder='property-project uuid'
          />

          <label css={LABEL_STYLE} htmlFor='adminScanId'>Scan ID (optional)</label>
          <input
            id='adminScanId'
            css={INPUT_STYLE}
            value={scanId}
            onChange={event => setScanId(event.target.value)}
            placeholder='scan uuid'
          />
        </div>

        <div css={CARD_STYLE}>
          <div css={INTRO_STYLE}>
            {loading ? 'Loading uploads...' : 'Viewing completed uploads across all users, with uploader details shown on each card.'}
          </div>

          <div css={LIST_STYLE}>
            {submissions.map(submission => (
              <div key={submission.id} css={ITEM_STYLE}>
                <div css={LABEL_STYLE}>{submission.originalFileName}</div>
                <div css={INTRO_STYLE}>Uploaded {new Date(submission.createdAt).toLocaleString()}</div>
                <div css={INTRO_STYLE}>Uploaded by: {submission.userName || 'Unknown'} ({submission.userId})</div>
                <div css={INTRO_STYLE}>Issue ID: {submission.id}</div>
                <div css={INTRO_STYLE}>Org: {submission.orgName || 'Unknown'} ({submission.orgId})</div>
                <div css={INTRO_STYLE}>Project: {submission.projectName || 'Unknown'} ({submission.propertyProjectId})</div>
                {submission.scanId ? <div css={INTRO_STYLE}>Scan ID: {submission.scanId}</div> : null}
                {submission.note ? <div css={INTRO_STYLE}>{submission.note}</div> : null}
                {submission.playbackUrl
                  ? (
                    <video
                      controls
                      preload='metadata'
                      style={{ width: '100%', maxHeight: '24rem', background: '#000' }}
                      src={submission.playbackUrl}
                    />
                  )
                  : null}
              </div>
            ))}
            {!loading && submissions.length === 0 ? <div css={INTRO_STYLE}>No uploads found.</div> : null}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
